import { useState } from "react";
import { CardView } from "../ui/Card";
import { Overlay } from "../ui/Overlay";
import type { DealProof } from "./dealLock";

/**
 * نشانِ «بازبینی کارت‌ها» روی پنلِ پایانِ دست، و برگه‌ی «کارت‌های همه» که لمسش
 * باز می‌کند — مشترکِ هر چهار میزِ آنلاین، همتای DealProofUI در اپ. سنجش کارِ
 * dealLock.ts است؛ این فقط نشانش می‌دهد. null = نشانی نیست (سرورِ قدیمی، یا
 * آغازِ دست را ندیدیم) — نبودنِ نشان بهتر از «نمی‌خواند»ِ دروغ است.
 */
export function DealProofChip({ proof, onOpen }: { proof: DealProof | null; onOpen: (p: DealProof) => void }) {
  if (!proof) return null;
  const ok = proof.result === "verified";
  return (
    // «پخش منصفانه بود» گنگ و «پخش بی‌تقلب بود» ماشینی بود (کاربر، ۱۸ اکتبر ۲۰۲۶): و کاربر خواست «خیلی
    // کوتاه‌تر»: «بازبینی کارت‌ها» و «ببین».
    <button className={`chip dealproof ${ok ? "ok" : "bad"}`} onClick={() => onOpen(proof)}>
      {ok ? <b>بازبینی کارت‌ها</b> : <><b>پخش جور درنیامد</b><span>چرا؟</span></>}
    </button>
  );
}

/**
 * برگه‌ی «کارت‌های همه». مالِ میز است، نه پنلِ پایانِ دست: سرور چند ثانیه بعد
 * دستِ تازه را شروع می‌کند و آن پنل را می‌بندد، و برگه‌ای که فرزندِ پنل بود با
 * آن بسته می‌شد — درست وقتی بازیکن دارد کارت‌ها را می‌خوانَد.
 */
export function DealSheet({ proof, name, onClose }: { proof: DealProof; name: (local: number) => string; onClose: () => void }) {
  const ok = proof.result === "verified";
  return (
    <Overlay label="دست همه در این پخش" onClose={onClose} className="dealsheet">
      <div className="dealtitle">{"دست همه"}</div>
      <div className="muted dealnote">
        {ok ? proof.note : "مهر پخش با این کارت‌ها جور درنیامد. اگر دوباره دیدی، به «پشتیبانی» خبر بده."}
      </div>
      <div className="dealrows">
        {proof.seats.map((cards, l) => <Row key={l} name={name(l)} cards={cards} />)}
        {proof.extra && proof.extra.cards.length > 0 && <Row name={proof.extra.name} cards={proof.extra.cards} />}
      </div>
      <button className="btn small" onClick={onClose}>فهمیدم</button>
    </Overlay>
  );
}

/** نشان + برگه با هم، برای میزی که یک جا می‌خواهدشان. */
export function useDealSheet(name: (local: number) => string) {
  const [open, setOpen] = useState<DealProof | null>(null);
  return {
    chip: (proof: DealProof | null) => <DealProofChip proof={proof} onOpen={setOpen} />,
    sheet: open ? <DealSheet proof={open} name={name} onClose={() => setOpen(null)} /> : null,
  };
}

function Row({ name, cards }: { name: string; cards: DealProof["seats"][number] }) {
  return (
    <div className="dealrow">
      <div className="dealname">{name}</div>
      <div className="dealcards">
        {cards.map((c, i) => <CardView key={i} card={c} width={34} />)}
      </div>
    </div>
  );
}
